/**
 * What would count as having done the job.
 *
 * Five objective kinds cover every mission this port can currently fly, and
 * each is a small pure function of the encounter state. Two properties matter
 * and both are tested:
 *
 * - **Latching.** A waypoint reached stays reached even if the flight drifts
 *   back out of the circle; an escort lost stays lost. Objectives that flicker
 *   make a mission that never ends.
 * - **Terminality.** `survival` objectives (protect this flight, get home)
 *   never "complete" on their own — they can only be pending or failed — while
 *   `primary` ones are what the mission is won by. Without that split, "the
 *   recon aeroplane is still alive" would be a win condition satisfied one tick
 *   after take-off.
 *
 * `returnToBase` and `landAtBase` are the two objectives with an ordering
 * constraint: they are only *armed* once every other primary is complete,
 * because the player starts on their own side of the lines — and, on a
 * Scramble, on their own aerodrome — so "be at home" is otherwise true at tick
 * zero. `HOMECOMING_KINDS` is that set, so the arming rule in `mission-run.ts`
 * and the list of kinds it applies to cannot drift apart.
 */

import type { EncounterState } from "../combat/encounter.ts";
import { horizontalDistanceM } from "../combat/route.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { isFriendlyTerritory, type FrontLine } from "./mission-world.ts";
import { isRaidPlan, raidIsOver, raidOutcomeOf, raidWentThePlayersWay, type RaidPlan } from "./raid.ts";

export const OBJECTIVE_KINDS = [
  "destroyAircraft",
  "destroyTargets",
  "protect",
  "protectTargets",
  "raidOutcome",
  "reachWaypoint",
  "returnToBase",
  "takeOff",
  "landAtBase",
] as const;

export type ObjectiveKind = (typeof OBJECTIVE_KINDS)[number];

/** The objectives that only arm once the rest of the job is done — see the module note. */
export const HOMECOMING_KINDS: readonly ObjectiveKind[] = ["returnToBase", "landAtBase"];

export function isHomecoming(objective: MissionObjective): boolean {
  return HOMECOMING_KINDS.includes(objective.kind);
}

export type ObjectiveRole = "primary" | "survival";

interface ObjectiveBase {
  readonly id: string;
  /** One line, HUD-length: `"Destroy 2 balloons"`. */
  readonly label: string;
  readonly role: ObjectiveRole;
}

export interface DestroyAircraftObjective extends ObjectiveBase {
  readonly kind: "destroyAircraft";
  readonly count: number;
  /** Restricts the tally to named flights; omitted means every hostile aeroplane. */
  readonly flightIds?: readonly string[];
}

export interface DestroyTargetsObjective extends ObjectiveBase {
  readonly kind: "destroyTargets";
  readonly count: number;
  readonly targetIds: readonly string[];
}

export interface ProtectObjective extends ObjectiveBase {
  readonly kind: "protect";
  readonly role: "survival";
  readonly flightIds: readonly string[];
  /**
   * How many of them may be lost before the objective fails. Defaults to none,
   * which is right for a single recon aeroplane and wrong for a bomber
   * formation: a raid that lost one Gotha and still levelled the factory did
   * what it was sent to do.
   */
  readonly allowedLosses?: number;
}

/**
 * `protect`, for the half of the roster that does not fly: the balloons a
 * BALLOON DEFENSE sortie is up for and the depot a raid is trying to level. It
 * fails the moment one of them catches fire rather than when the fire finishes,
 * because a burning gasbag or a burning shed is already lost and waiting out its
 * `burnSeconds` to say so would leave a decided mission running.
 */
export interface ProtectTargetsObjective extends ObjectiveBase {
  readonly kind: "protectTargets";
  readonly role: "survival";
  readonly targetIds: readonly string[];
  /** How many may be lost before the objective fails. Defaults to none. */
  readonly allowedLosses?: number;
}

/**
 * How the bombing raid itself came out — the raid family's decisive objective,
 * and the only one that can be settled by something the player does not do.
 *
 * It cannot be judged until the raid is **over** (`raid.ts`'s `raidIsOver`),
 * because "the targets are still standing" is true from tick zero. Judging it
 * on the targets alone leaves an escort mission whose raid dropped everything
 * and missed running for ever: nothing has been destroyed, so no objective
 * completes, and the player is alive, so none fails.
 */
export interface RaidOutcomeObjective extends ObjectiveBase {
  readonly kind: "raidOutcome";
  readonly role: "primary";
}

export interface ReachWaypointObjective extends ObjectiveBase {
  readonly kind: "reachWaypoint";
  readonly flightId: string;
  readonly position: Vec3;
  readonly radiusM: number;
}

export interface ReturnToBaseObjective extends ObjectiveBase {
  readonly kind: "returnToBase";
  readonly role: "primary";
  readonly flightId: string;
}

/** Get off the ground. The first half of a Scramble, and the only objective that can start complete-in-reverse. */
export interface TakeOffObjective extends ObjectiveBase {
  readonly kind: "takeOff";
  readonly role: "primary";
  readonly flightId: string;
  /** Height above the field, metres, that counts as away. */
  readonly clearanceM: number;
}

/**
 * Put it back on the ground at the aerodrome, in one piece, and stop.
 *
 * This is the original's own end-of-mission: *"you will receive more points for
 * landing at your aerodrome"* and *"Bonus points for landing at your own
 * aerodrome upon completion of your mission"* (04-gameplay.md), with the
 * Mission Builder making the home field the last path point. Armed like
 * `returnToBase`, and for the same reason — a flight that starts on its own
 * aerodrome would otherwise satisfy it before it had rolled a metre.
 */
export interface LandAtBaseObjective extends ObjectiveBase {
  readonly kind: "landAtBase";
  readonly role: "primary";
  readonly flightId: string;
  readonly position: Vec3;
  readonly radiusM: number;
}

export type MissionObjective =
  | DestroyAircraftObjective
  | DestroyTargetsObjective
  | ProtectObjective
  | ProtectTargetsObjective
  | RaidOutcomeObjective
  | ReachWaypointObjective
  | ReturnToBaseObjective
  | TakeOffObjective
  | LandAtBaseObjective;

export type ObjectiveStatus = "pending" | "complete" | "failed";

export interface ObjectiveState {
  readonly id: string;
  readonly status: ObjectiveStatus;
  /** How much of it is done, in the objective's own units (kills, survivors, 0/1). */
  readonly progress: number;
  readonly required: number;
}

export interface ObjectiveContext {
  readonly encounter: EncounterState;
  readonly playerId: string;
  readonly frontLine: FrontLine;
  /** True once every other primary objective is complete. Gates `returnToBase`. */
  readonly armed: boolean;
  /** The mission's raid, for a `raidOutcome` objective to read. */
  readonly raid?: RaidPlan;
}

function settled(state: ObjectiveState): boolean {
  return state.status !== "pending";
}

function tally(id: string, progress: number, required: number): ObjectiveState {
  return { id, progress, required, status: progress >= required ? "complete" : "pending" };
}

function destroyedAircraft(context: ObjectiveContext, flightIds?: readonly string[]): number {
  return context.encounter.aircraft.filter(
    (entry) =>
      entry.status === "destroyed" &&
      (flightIds ? flightIds.includes(entry.id) : entry.side === "hostile"),
  ).length;
}

function destroyedTargets(context: ObjectiveContext, targetIds: readonly string[]): number {
  return context.encounter.targets.filter((entry) => entry.destroyed && targetIds.includes(entry.id)).length;
}

function survivorsOf(context: ObjectiveContext, flightIds: readonly string[]): number {
  return context.encounter.aircraft.filter(
    (entry) => flightIds.includes(entry.id) && entry.status !== "destroyed",
  ).length;
}

/** Alight counts as lost — see `ProtectTargetsObjective`. */
function intactTargets(context: ObjectiveContext, targetIds: readonly string[]): number {
  return context.encounter.targets.filter(
    (entry) => targetIds.includes(entry.id) && !entry.destroyed && !entry.burning,
  ).length;
}

function evaluateProtectTargets(
  objective: ProtectTargetsObjective,
  context: ObjectiveContext,
): ObjectiveState {
  const intact = intactTargets(context, objective.targetIds);
  const required = Math.max(0, objective.targetIds.length - (objective.allowedLosses ?? 0));
  return {
    id: objective.id,
    progress: intact,
    required,
    status: intact < required ? "failed" : "pending",
  };
}

function evaluateRaidOutcome(
  objective: RaidOutcomeObjective,
  context: ObjectiveContext,
): ObjectiveState {
  const plan = context.raid;
  // A mission that carries this objective without a plan is a generator bug, not
  // a state to absorb: report it as pending rather than inventing a verdict.
  if (!isRaidPlan(plan) || !raidIsOver(plan, context.encounter)) {
    return { id: objective.id, status: "pending", progress: 0, required: 1 };
  }
  // Which way the player needed it to go is the *plan's* `role`, not a second
  // field on the objective that could disagree with it.
  const wentWell = raidWentThePlayersWay(raidOutcomeOf(plan, context.encounter));
  return { id: objective.id, status: wentWell ? "complete" : "failed", progress: wentWell ? 1 : 0, required: 1 };
}

function evaluateReachWaypoint(
  objective: ReachWaypointObjective,
  context: ObjectiveContext,
): ObjectiveState {
  const flight = context.encounter.aircraft.find((entry) => entry.id === objective.flightId);
  if (!flight || flight.status === "destroyed") {
    return { id: objective.id, status: "failed", progress: 0, required: 1 };
  }
  const arrived = horizontalDistanceM(flight.sim.position, objective.position) <= objective.radiusM;
  return tally(objective.id, arrived ? 1 : 0, 1);
}

function evaluateReturnToBase(
  objective: ReturnToBaseObjective,
  context: ObjectiveContext,
): ObjectiveState {
  const flight = context.encounter.aircraft.find((entry) => entry.id === objective.flightId);
  if (!flight || flight.status === "destroyed") {
    return { id: objective.id, status: "failed", progress: 0, required: 1 };
  }
  const home = context.armed && isFriendlyTerritory(context.frontLine, flight.sim.position.z);
  return tally(objective.id, home ? 1 : 0, 1);
}

function evaluateTakeOff(objective: TakeOffObjective, context: ObjectiveContext): ObjectiveState {
  const flight = context.encounter.aircraft.find((entry) => entry.id === objective.flightId);
  if (!flight || flight.status === "destroyed" || flight.sim.gear.collapsed) {
    return { id: objective.id, status: "failed", progress: 0, required: 1 };
  }
  const away = !flight.sim.readout.grounded && flight.sim.readout.altitudeAglM >= objective.clearanceM;
  return tally(objective.id, away ? 1 : 0, 1);
}

function evaluateLandAtBase(objective: LandAtBaseObjective, context: ObjectiveContext): ObjectiveState {
  const flight = context.encounter.aircraft.find((entry) => entry.id === objective.flightId);
  if (!flight || flight.status === "destroyed") {
    return { id: objective.id, status: "failed", progress: 0, required: 1 };
  }
  const down =
    context.armed &&
    flight.sim.gear.stopped &&
    !flight.sim.gear.collapsed &&
    horizontalDistanceM(flight.sim.position, objective.position) <= objective.radiusM;
  return tally(objective.id, down ? 1 : 0, 1);
}

function evaluateFresh(objective: MissionObjective, context: ObjectiveContext): ObjectiveState {
  switch (objective.kind) {
    case "destroyAircraft":
      return tally(objective.id, destroyedAircraft(context, objective.flightIds), objective.count);
    case "destroyTargets":
      return tally(objective.id, destroyedTargets(context, objective.targetIds), objective.count);
    case "protect": {
      const alive = survivorsOf(context, objective.flightIds);
      const required = Math.max(0, objective.flightIds.length - (objective.allowedLosses ?? 0));
      return {
        id: objective.id,
        progress: alive,
        required,
        status: alive < required ? "failed" : "pending",
      };
    }
    case "protectTargets":
      return evaluateProtectTargets(objective, context);
    case "raidOutcome":
      return evaluateRaidOutcome(objective, context);
    case "reachWaypoint":
      return evaluateReachWaypoint(objective, context);
    case "returnToBase":
      return evaluateReturnToBase(objective, context);
    case "takeOff":
      return evaluateTakeOff(objective, context);
    case "landAtBase":
      return evaluateLandAtBase(objective, context);
  }
}

/** The two survival kinds count *down*, so the "progress only rises" rule must not apply to them. */
function countsDown(objective: MissionObjective): boolean {
  return objective.kind === "protect" || objective.kind === "protectTargets";
}

/**
 * One objective, one tick. Anything already settled stays settled — the whole
 * point of passing the previous state in.
 */
export function evaluateObjective(
  objective: MissionObjective,
  context: ObjectiveContext,
  previous: ObjectiveState,
): ObjectiveState {
  if (settled(previous)) return previous;
  const next = evaluateFresh(objective, context);
  // Progress on a counting objective can only go up: an aeroplane cannot
  // become un-destroyed, and reading a lower number would mean a bug elsewhere.
  return { ...next, progress: Math.max(next.progress, countsDown(objective) ? 0 : previous.progress) };
}

export function initialObjectiveState(objective: MissionObjective): ObjectiveState {
  switch (objective.kind) {
    case "destroyAircraft":
      return { id: objective.id, status: "pending", progress: 0, required: objective.count };
    case "destroyTargets":
      return { id: objective.id, status: "pending", progress: 0, required: objective.count };
    case "protect":
      return {
        id: objective.id,
        status: "pending",
        progress: objective.flightIds.length,
        required: Math.max(0, objective.flightIds.length - (objective.allowedLosses ?? 0)),
      };
    case "protectTargets":
      return {
        id: objective.id,
        status: "pending",
        progress: objective.targetIds.length,
        required: Math.max(0, objective.targetIds.length - (objective.allowedLosses ?? 0)),
      };
    default:
      return { id: objective.id, status: "pending", progress: 0, required: 1 };
  }
}

/** `"2 / 3"` for a counting objective, `""` for a yes/no one. */
export function objectiveProgressLabel(state: ObjectiveState): string {
  return state.required > 1 ? `${state.progress} / ${state.required}` : "";
}
